// Otto Lister-Detail-Tab "Bilder" — kombiniert Auswahl + Freigabe der Vario-DMS-Bilder eines
// Artikels in einem Tab (läuft auch im Vario-Slot): Released-Toggle, Alt-Text, Reihenfolge.
// Speichern triggert otto-release-images → Worker otto.ensure_dms_share (UNLIMITED-DMS-Share
// + Alt-Text + uiSort, public_url zurück in otto.article_allowed_images).

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import { httpErrorText } from '../../lib/session-expiry.js';

export interface ImagesTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  registerSaver?: (id: string, fn: () => Promise<void>) => void;
}

export interface ImagesTabListing {
  vario_article_id: string;
  mode?: 'parent' | 'child' | 'standalone' | 'ordering';
  parentListingId?: string | null;
}

interface ImageRow {
  resourceId: string;
  alias: string;
  description: string | null;
  mimeType: string;
  fileSize: number | null;
  released: boolean;
  sortOrder: number | null;
  varioUiSort: number | null;
  publicUrl: string | null;
  // Lokaler Edit-State:
  altText: string;
}

const COMPONENT_STYLES = `
  .kli-shell { padding: 1em; display: flex; flex-direction: column; gap: 0.8em; }
  .kli-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.6em 0.8em;
    background: var(--v-color-surface, #fff);
    border: 1px solid var(--v-color-border, #e0e0e0);
    border-radius: var(--v-border-radius-card, 12px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .kli-list { display: flex; flex-direction: column; gap: 0.4em; }
  .kli-row {
    display: grid;
    grid-template-columns: 30px 96px 1fr auto;
    gap: 1em;
    align-items: center;
    padding: 0.6em 0.8em;
    border: 1px solid var(--v-color-border, #e0e0e0);
    border-radius: 8px;
    background: var(--v-color-surface, #fff);
    border-left: 3px solid var(--v-positive, #84b933);
  }
  .kli-thumb {
    width: 96px; height: 96px; object-fit: cover;
    border-radius: 6px; border: 1px solid var(--v-color-border, #ddd);
    background: var(--v-color-surface-muted, #f7f7f7);
  }
  .kli-thumb.kli-thumb-loading {
    background: linear-gradient(110deg, #f0f0f0 8%, #e3e3e3 18%, #f0f0f0 33%);
    background-size: 200% 100%;
    animation: kli-shimmer 1.5s linear infinite;
  }
  @keyframes kli-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  .kli-meta { display: flex; flex-direction: column; gap: 0.35em; min-width: 0; }
  .kli-alias { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .kli-slot {
    display: inline-block; padding: 0.1em 0.45em; margin-right: 0.3em;
    background: var(--v-positive-soft, #e8f3d6); color: var(--v-positive, #84b933);
    border-radius: 4px; font-size: 0.82em; font-weight: 600;
  }
  .kli-sub { font-size: 0.78em; color: var(--v-color-text-muted, #666); }
  .kli-alt-input {
    width: 100%; padding: 0.4em 0.6em;
    border: 1px solid var(--v-color-border, #ddd); border-radius: 4px;
    font-size: 0.92em;
  }
  .kli-actions { display: flex; flex-direction: column; gap: 0.3em; align-items: center; }
  .kli-empty { padding: 1.5em; text-align: center; color: var(--v-color-text-muted, #666); }
  .kli-tabs { display: flex; gap: 0.3em; border-bottom: 1px solid var(--v-color-border, #e0e0e0); margin-bottom: 0.4em; }
  .kli-tab {
    padding: 0.5em 1em; border: none; background: none; cursor: pointer;
    font-size: 0.95em; color: var(--v-color-text-muted, #666);
    border-bottom: 2px solid transparent;
  }
  .kli-tab.kli-tab--active { color: var(--v-color-text, #222); border-bottom-color: var(--v-positive, #84b933); font-weight: 600; }
  .kli-toggle-row {
    display: grid; grid-template-columns: 28px 64px 1fr; gap: 0.8em; align-items: center;
    padding: 0.6em 0.8em; border-bottom: 1px solid var(--v-color-border-soft, #eee);
  }
  .kli-toggle-thumb { width: 64px; height: 64px; object-fit: cover; border-radius: 6px; border: 1px solid var(--v-color-border, #ddd); background: var(--v-color-surface-muted, #f7f7f7); }
  /* Nicht-freigegebene Zeile: gedimmt, neutraler statt gruener Rand. */
  .kli-row--off { opacity: 0.6; border-left-color: var(--v-color-border, #e0e0e0); }
  .kli-row-toggle { width: 18px; height: 18px; cursor: pointer; justify-self: center; }
  /* Upload-Zone. */
  .kli-upload-zone {
    border: 2px dashed var(--v-color-border, #ccc); border-radius: 8px;
    padding: 1em 1.2em; display: flex; align-items: center; gap: 0.8em;
    cursor: pointer; transition: border-color 0.15s, background 0.15s;
    background: var(--v-color-surface, #fff);
  }
  .kli-upload-zone:hover, .kli-upload-zone.kli-upload-zone--drag { border-color: var(--v-positive, #84b933); background: var(--v-positive-soft, #f0f7e6); }
  .kli-upload-zone input[type=file] { display: none; }
  .kli-upload-zone-label { font-size: 0.9em; color: var(--v-color-text-muted, #666); }
`;

// Matrix-CSS lebt jetzt scoped in styles-lister.css (RD5: .otto-lv2 .kl-matrix …).

interface MatrixTarget { articleId: string; label: string; isParent: boolean }
interface MatrixImage {
  resourceId: string;
  owningArticleId: string;
  alias: string;
  mimeType: string;
  publicUrl: string | null;
  seoTitle: string | null;
  assignedTo: string[];
  // RD0-Backend: pro Bild die Anzeige-Reihenfolge je Ziel-Artikel (sort_order).
  orderByTarget?: Record<string, number>;
}

// Spalten-Key der Vater-Spalte im lokalen order-State. Wird beim Speichern auf die
// echte Vater-Artikel-ID gemappt (PARENT_COL → parentArticleId).
const PARENT_COL = '__parent__';

async function callFn<T>(
  ctx: ImagesTabCtx,
  name: string,
  body: unknown,
): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : httpErrorText(res.status) };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export async function renderImagesTab(
  host: HTMLElement,
  ctx: ImagesTabCtx,
  listing: ImagesTabListing,
): Promise<void> {
  host.innerHTML = `<style>${COMPONENT_STYLES}</style><div class="kli-shell"><div class="kli-empty">Lade Vario-DMS-Bilder …</div></div>`;

  const mode = listing.mode ?? 'standalone';
  if (mode === 'parent' && listing.parentListingId) {
    return renderParentMatrix(host, ctx, listing.parentListingId);
  }
  // 'child' und 'ordering' nutzen denselben Render-Pfad wie 'standalone', aber OHNE
  // "Freigeben"-Tab (Reihenfolge-only); 'child' zusätzlich mit Info-Banner.
  // 'standalone' behält beide Tabs unverändert.
  const orderingOnly = mode === 'child' || mode === 'ordering';
  const showBanner = mode === 'child';

  type Loaded = Omit<ImageRow, 'altText'>;
  const res = await callFn<{ ok: boolean; images: Loaded[] }>(ctx, 'otto-article-images', {
    articleId: listing.vario_article_id,
  });
  if (!res.ok) {
    host.innerHTML = `<div class="kli-shell"><div class="v-text-error v-pa-md">Fehler: ${escapeHtml(res.error)}</div></div>`;
    return;
  }

  // rows hält ALLE Bilder (released + nicht released) als gemeinsamen State.
  // Der "Aktive Bilder"-Tab filtert beim Rendern auf released und sortiert nach
  // sortOrder; der "Freigeben"-Tab zeigt alle mit Toggle.
  const rows: ImageRow[] = (res.value.images ?? []).map((img) => ({
    ...img,
    altText: typeof img.description === 'string' ? img.description : '',
  }));
  // Released-Bilder einmal initial nach vorhandenem sortOrder/varioUiSort ordnen
  // und auf 0..N normalisieren — danach ist sortOrder die alleinige Quelle der
  // Push-Reihenfolge.
  rows
    .filter((r) => r.released)
    .sort((a, b) => (a.sortOrder ?? a.varioUiSort ?? 0) - (b.sortOrder ?? b.varioUiSort ?? 0))
    .forEach((r, idx) => { r.sortOrder = idx; });
  // Eine Ansicht (kein Tab mehr): standalone zeigt ALLE Bilder mit an-Otto-Toggle +
  // Reihenfolge; orderingOnly (child/ordering) zeigt nur freigegebene, reorder-only.
  let dirty = false;

  // Cache der via Proxy geladenen Vorschau-Blob-URLs pro resourceId. Lebt fuer die
  // Dauer dieses Dialogs. Verhindert, dass jeder Tab-Wechsel / Checkbox-Toggle
  // dieselben Bilder erneut durch den DMS-Proxy laedt — sonst Vario-429 (Rate-Limit).
  const previewCache = new Map<string, string>();

  // Liefert das <img>-Markup fuer eine Vorschau: bevorzugt die public_url (freigegeben)
  // bzw. eine bereits gecachte Proxy-Blob-URL; sonst einen Lazy-Platzhalter, den
  // loadPreviewsViaProxy spaeter EINMAL befuellt und cacht.
  function previewImg(r: ImageRow, thumbClass: string): string {
    const ready = r.publicUrl ?? previewCache.get(r.resourceId) ?? null;
    if (ready) return `<img class="${thumbClass}" src="${escapeHtml(ready)}" alt="">`;
    return `<img class="${thumbClass} kli-thumb-loading img-proxy-lazy" data-rid="${escapeHtml(r.resourceId)}" alt="">`;
  }

  // Released-Bilder in Push-Reihenfolge (sortOrder aufsteigend, stabil).
  function releasedSorted(): ImageRow[] {
    return rows
      .filter((r) => r.released)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  // Vollständige releases-Payload aus dem gemeinsamen State — von BEIDEN
  // Speichern-Pfaden genutzt. Sendet alle released-Bilder mit fortlaufendem
  // sortOrder (0..N nach aktueller Push-Reihenfolge) + erhaltenem altText.
  // Was hier fehlt (= nicht released), löscht das Backend (Diff-Upsert).
  function buildReleases(): Array<{ varioResourceId: string; sortOrder: number; mimeType: string; alias: string; altText: string }> {
    return releasedSorted().map((r, idx) => ({
      varioResourceId: r.resourceId,
      sortOrder: idx,
      mimeType: r.mimeType,
      alias: r.alias,
      altText: r.altText.trim(),
    }));
  }

  // Anzeige-Reihenfolge aller Bilder: freigegebene zuerst (nach sortOrder), dann die
  // nicht-freigegebenen (alphabetisch, stabil). Im orderingOnly-Modus nur die freigegebenen.
  function displayRows(): ImageRow[] {
    if (orderingOnly) return releasedSorted();
    const off = rows.filter((r) => !r.released).sort((a, b) => a.alias.localeCompare(b.alias, 'de'));
    return [...releasedSorted(), ...off];
  }

  function renderList(): void {
    const shell = host.querySelector<HTMLElement>('.kli-body');
    if (!shell) return;
    if (rows.length === 0) {
      shell.innerHTML = `
        <div class="kli-header"><strong>Bilder</strong></div>
        <div class="kli-empty">
          ${orderingOnly
            ? 'Für diese Variante sind noch keine Bilder zugeordnet. Die Zuordnung erfolgt im <strong>Variantenvater</strong>.'
            : 'Noch keine Bilder am Artikel. Lade oben ein Bild hoch (JPG/PNG) oder pflege Bilder in Vario.'}
        </div>`;
      return;
    }
    const list = displayRows();
    const releasedCount = rows.filter((r) => r.released).length;
    const countLabel = orderingOnly
      ? `${releasedCount} ${releasedCount === 1 ? 'Bild' : 'Bilder'}`
      : `${releasedCount} von ${rows.length} an Otto`;
    shell.innerHTML = `
      <div class="kli-header">
        <div>
          <strong>Bilder</strong>
          <span class="v-text-muted" style="font-size:0.85em;margin-left:0.5em;">${countLabel}</span>
        </div>
      </div>
      <div class="kli-list">
        ${list.map((r) => renderRow(r)).join('')}
      </div>
    `;
    wireRows(shell);
  }

  // Eine Zeile pro Bild. Freigegebene: an-Otto-Toggle (checked) + Slot-Label ("Hauptbild"/
  // "Bild N" = 1:1 die Push-Reihenfolge) + Alt-Text + Up/Down. Nicht freigegebene: Toggle
  // (unchecked) + gedimmt, nur Alias/Typ. orderingOnly: kein Toggle (reine Reihenfolge).
  function renderRow(r: ImageRow): string {
    const released = releasedSorted();
    const relIdx = r.released ? released.findIndex((x) => x.resourceId === r.resourceId) : -1;
    const relTotal = released.length;
    const sizeKb = r.fileSize ? `${Math.round(r.fileSize / 1024)} KB` : '';
    const previewInner = previewImg(r, 'kli-thumb');
    const slotLabel = relIdx === 0 ? 'Hauptbild' : `Bild ${relIdx}`;
    const toggleCell = orderingOnly
      ? '<span></span>'
      : `<input type="checkbox" class="kli-row-toggle" data-rid="${escapeHtml(r.resourceId)}" ${r.released ? 'checked' : ''} title="${r.released ? 'An Otto — abwählen zum Entfernen' : 'An Otto senden'}">`;
    const metaInner = r.released
      ? `<div class="kli-alias" title="${escapeHtml(r.alias)}"><span class="kli-slot">${slotLabel}</span> · ${escapeHtml(r.alias)}</div>
          <div class="kli-sub">${escapeHtml(r.mimeType)}${sizeKb ? ` · ${sizeKb}` : ''}</div>
          <input type="text" class="kli-alt-input" data-rid="${escapeHtml(r.resourceId)}" value="${escapeHtml(r.altText)}" placeholder="Alt-Text für ${slotLabel} (Screenreader / SEO, geht ins Vario-DMS) …" maxlength="250">`
      : `<div class="kli-alias" title="${escapeHtml(r.alias)}">${escapeHtml(r.alias)}</div>
          <div class="kli-sub">${escapeHtml(r.mimeType)}${sizeKb ? ` · ${sizeKb}` : ''}</div>`;
    const actionsInner = r.released
      ? `<button type="button" class="v-button v-button--ghost v-icon-only kli-up" data-rid="${escapeHtml(r.resourceId)}" title="Nach oben" ${relIdx === 0 ? 'disabled' : ''}>
            <span class="v-icon fa-regular fa-arrow-up"></span>
          </button>
          <button type="button" class="v-button v-button--ghost v-icon-only kli-down" data-rid="${escapeHtml(r.resourceId)}" title="Nach unten" ${relIdx === relTotal - 1 ? 'disabled' : ''}>
            <span class="v-icon fa-regular fa-arrow-down"></span>
          </button>`
      : '';
    return `
      <div class="kli-row${r.released ? '' : ' kli-row--off'}" data-rid="${escapeHtml(r.resourceId)}">
        ${toggleCell}
        ${previewInner}
        <div class="kli-meta">
          ${metaInner}
        </div>
        <div class="kli-actions">${actionsInner}</div>
      </div>`;
  }

  function wireRows(shell: HTMLElement): void {
    // an-Otto-Toggle (nur standalone): released umschalten; neu freigegeben → ans Ende.
    shell.querySelectorAll<HTMLInputElement>('input.kli-row-toggle').forEach((cb) => {
      cb.addEventListener('change', () => {
        const r = rows.find((x) => x.resourceId === cb.dataset['rid']);
        if (!r) return;
        r.released = cb.checked;
        if (cb.checked) {
          const maxSort = rows.filter((x) => x.released && x !== r)
            .reduce((m, x) => Math.max(m, x.sortOrder ?? 0), -1);
          r.sortOrder = maxSort + 1;
        }
        dirty = true;
        renderList();
      });
    });
    shell.querySelectorAll<HTMLInputElement>('input.kli-alt-input').forEach((inp) => {
      inp.addEventListener('input', () => {
        const r = rows.find((x) => x.resourceId === inp.dataset['rid']);
        if (r) { r.altText = inp.value; dirty = true; }
      });
    });
    shell.querySelectorAll<HTMLButtonElement>('button.kli-up').forEach((b) => {
      b.addEventListener('click', () => move(b.dataset['rid'] ?? '', -1));
    });
    shell.querySelectorAll<HTMLButtonElement>('button.kli-down').forEach((b) => {
      b.addEventListener('click', () => move(b.dataset['rid'] ?? '', +1));
    });
    void loadPreviewsViaProxy(shell);
  }

  function move(rid: string, delta: number): void {
    const active = releasedSorted();
    const pos = active.findIndex((r) => r.resourceId === rid);
    const newPos = pos + delta;
    if (pos < 0 || newPos < 0 || newPos >= active.length) return;
    // sortOrder der beiden Nachbarn tauschen — danach normalisieren wir auf 0..N,
    // damit die Werte kompakt bleiben.
    const a = active[pos]!;
    const b = active[newPos]!;
    const tmp = a.sortOrder ?? pos;
    a.sortOrder = b.sortOrder ?? newPos;
    b.sortOrder = tmp;
    releasedSorted().forEach((r, idx) => { r.sortOrder = idx; });
    dirty = true;
    renderList();
  }

  async function loadPreviewsViaProxy(shell: HTMLElement): Promise<void> {
    const imgs = Array.from(shell.querySelectorAll<HTMLImageElement>('img.img-proxy-lazy'));
    // Sequenziell (Concurrency 1) statt parallel — der DMS-Proxy macht pro Bild einen
    // Vario-DMS-Call + OAuth-Refresh; ein Burst ueberschreitet das Vario-Rate-Limit (429).
    // Jede geladene Blob-URL wird in previewCache abgelegt, sodass Tab-Wechsel / Toggles
    // dasselbe Bild NICHT erneut laden (previewImg() rendert dann direkt aus dem Cache).
    for (const imgEl of imgs) {
      const rid = imgEl.dataset['rid'];
      if (!rid) continue;
      if (!imgEl.isConnected) continue;
      // Schon im Cache (z.B. Race mit einem frueheren Render): direkt setzen, kein Fetch.
      const cached = previewCache.get(rid);
      if (cached) {
        imgEl.src = cached;
        imgEl.classList.remove('kli-thumb-loading');
        continue;
      }
      try {
        const r = await fetch(`${ctx.supabaseUrl}/functions/v1/vario-dms-image-proxy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
          body: JSON.stringify({ articleId: listing.vario_article_id, resourceId: rid, marketplace: 'otto' }),
        });
        if (!r.ok) continue;
        const url = URL.createObjectURL(await r.blob());
        previewCache.set(rid, url);
        if (!imgEl.isConnected) continue;
        imgEl.src = url;
        imgEl.classList.remove('kli-thumb-loading');
      } catch (e) {
        console.warn('[otto-list-images] preview proxy failed for', rid, (e as Error).message);
      }
    }
  }

  // Speichern über die Save-Registry des Detail-Panes (oberer Per-Tab-Button „Bilder speichern").
  // Konsistent zu doSaveMatrix: WIRFT bei Fehler (runSavers meldet aggregiert), kein eigener
  // Erfolgs-Toast (runSavers toastet „Bilder gespeichert"), kein eigener Button mehr.
  async function doSave(): Promise<void> {
    // Vollständige released-Liste aus dem gemeinsamen State (Datenverlust-Schutz):
    // erhält Alt-Texte + Reihenfolge, löscht nur wirklich abgewählte Bilder.
    const releases = buildReleases();
    const res = await callFn<{ ok: boolean; inserted: number; updated: number; deleted: number; enqueuedShareJobs?: number; enqueuedMetaJobs?: number }>(
      ctx,
      'otto-release-images',
      { articleId: listing.vario_article_id, releases },
    );
    if (!res.ok) throw new Error(res.error);
    dirty = false;
    void renderImagesTab(host, ctx, listing);
  }

  // ── Upload-Zone, nur standalone. Datei → otto-article-image-upload
  // (multipart, KEIN Content-Type-Header → Browser setzt boundary). accept nur JPG/PNG
  // (Otto-Formate). Erfolg → Tab-Neustart (frische Bildliste, neues Bild unfreigegeben).
  function renderUploadZone(): string {
    return `
      <div class="kli-upload-zone" id="kli-upload-zone" role="button" tabindex="0"
           aria-label="Bild hochladen — klicken oder hierher ziehen">
        <input type="file" id="kli-upload-input" accept="image/jpeg,image/png">
        <span class="v-icon fa-regular fa-cloud-arrow-up" style="font-size:1.4em;color:var(--v-positive,#84b933);"></span>
        <div class="kli-upload-zone-label">
          <strong>Bild hochladen</strong><br>
          <span>Klicken oder Datei hier ablegen (JPG, PNG)</span>
        </div>
        <span id="kli-upload-busy" hidden><span class="v-icon fa-regular fa-spinner-third fa-spin"></span></span>
      </div>`;
  }

  function wireUploadZone(shell: HTMLElement): void {
    const zone = shell.querySelector<HTMLElement>('#kli-upload-zone');
    const inp = shell.querySelector<HTMLInputElement>('#kli-upload-input');
    const busy = shell.querySelector<HTMLElement>('#kli-upload-busy');
    if (!zone || !inp) return;
    const zoneEl: HTMLElement = zone;

    zoneEl.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('input[type=file]')) return;
      inp.click();
    });
    zoneEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inp.click(); }
    });
    zoneEl.addEventListener('dragover', (e) => { e.preventDefault(); zoneEl.classList.add('kli-upload-zone--drag'); });
    zoneEl.addEventListener('dragleave', () => zoneEl.classList.remove('kli-upload-zone--drag'));
    zoneEl.addEventListener('drop', (e) => {
      e.preventDefault();
      zoneEl.classList.remove('kli-upload-zone--drag');
      const file = e.dataTransfer?.files[0];
      if (file) void doUpload(file);
    });
    inp.addEventListener('change', () => {
      const file = inp.files?.[0];
      if (file) void doUpload(file);
    });

    async function doUpload(file: File): Promise<void> {
      if (busy) busy.hidden = false;
      zoneEl.style.pointerEvents = 'none';
      const fd = new FormData();
      fd.append('file', file);
      fd.append('articleId', listing.vario_article_id);
      try {
        const r = await fetch(`${ctx.supabaseUrl}/functions/v1/otto-article-image-upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${ctx.session.token}` },
          // KEIN Content-Type — Browser setzt multipart/form-data + boundary automatisch.
          body: fd,
        });
        if (!r.ok) {
          const t = await r.text().catch(() => '');
          let msg = httpErrorText(r.status);
          try { const j = JSON.parse(t); if (j?.error?.message) msg = j.error.message; } catch { /* keep */ }
          showToast(`Upload fehlgeschlagen: ${msg}`, 'error');
          return;
        }
        showToast('Bild hochgeladen.', 'success');
        // Tab neu laden (frische Bildliste; neues Bild erscheint unfreigegeben).
        void renderImagesTab(host, ctx, listing);
      } catch (e) {
        showToast(`Upload fehlgeschlagen: ${(e as Error).message}`, 'error');
      } finally {
        if (busy) busy.hidden = true;
        zoneEl.style.pointerEvents = '';
      }
    }
  }

  function render(): void {
    // child: Info-Banner — Bild-AUSWAHL passiert im Variantenvater, hier nur Reihenfolge/Hauptbild.
    const bannerHtml = showBanner
      ? `<div class="v-banner v-banner-info" style="margin:0.6em;"><span class="v-icon fa-light fa-circle-info"></span><div>Welche Bilder hier erscheinen, legst du im <strong>Variantenvater</strong> fest. Hier bestimmst du nur Reihenfolge &amp; Hauptbild.</div></div>`
      : '';
    // Upload nur standalone; orderingOnly (child/ordering) = Varianten-Kontext, reine Reihenfolge.
    const uploadHtml = orderingOnly ? '' : renderUploadZone();
    host.innerHTML = `
      <style>${COMPONENT_STYLES}</style>
      <div class="kli-shell">
        ${bannerHtml}
        ${uploadHtml}
        <div class="kli-body"></div>
      </div>`;
    if (!orderingOnly) {
      const shell = host.querySelector<HTMLElement>('.kli-shell');
      if (shell) wireUploadZone(shell);
    }
    renderList();
  }

  // doSave als 'images'-Saver registrieren → der obere Per-Tab-Button „Bilder speichern" führt
  // im Standalone/Kind-Pfad genau das aus, was zuvor der interne Button tat.
  ctx.registerSaver?.('images', doSave);
  render();
}

// ============================================================================
// Variantenvater: Bild-Zuordnungs-Matrix (RD5) — Order-Zellen statt Checkboxen.
// Zeilen = alle Bilder Vater+Kinder, Spalten = Vater + jedes Kanal-Kind, Zelle =
// "+"-Button (zuordnen) ODER nummerierte "on"-Zelle (= Anzeige-Reihenfolge je
// Spalte) mit ▲▼-Mini (vor/zurück). Sub-Tab "Reihenfolge (Vater)" mountet
// renderImagesTab im 'ordering'-Modus auf den Vater-Artikel (bestehende
// Aktive-Bilder-Up/Down-Logik, ohne Banner/Freigeben).
//
// Lokaler State (1:1 wie Mockup colMax/ordCell/ordAdd/ordRm/ordSwap):
//   order: Map<resourceId, Record<col, rank>>  — col = PARENT_COL für die Vater-
//   Spalte (→ parentArticleId beim Speichern), sonst die Kind-Artikel-ID; rank
//   1-basiert je Spalte. Initialisiert aus orderByTarget (Backend, sort_order).
// ============================================================================
async function renderParentMatrix(host: HTMLElement, ctx: ImagesTabCtx, parentListingId: string): Promise<void> {
  host.innerHTML = `<div class="otto-lv2"><div class="kli-empty">Lade Bild-Zuordnung …</div></div>`;
  const res = await callFn<{ ok: boolean; targets: MatrixTarget[]; images: MatrixImage[] }>(
    ctx, 'otto-variant-image-matrix', { action: 'list', parentListingId });
  if (!res.ok) { host.innerHTML = `<div class="otto-lv2"><div class="v-text-error v-pa-md">Fehler: ${escapeHtml(res.error)}</div></div>`; return; }
  const targets = res.value.targets;
  const images = res.value.images;
  const parentArticleId = targets.find((t) => t.isParent)?.articleId ?? '';

  // Spalten-Keys: Vater → PARENT_COL, Kanal-Kinder → ihre Artikel-ID. Reihenfolge =
  // targets-Reihenfolge (Vater zuerst). colKeyFor()/articleIdForCol() mappen hin und her.
  const colKeyFor = (t: MatrixTarget): string => (t.isParent ? PARENT_COL : t.articleId);
  const articleIdForCol = (col: string): string => (col === PARENT_COL ? parentArticleId : col);
  const cols = targets.map(colKeyFor);

  // order: pro Bild ein Record<col, rank>. Init aus orderByTarget (Backend liefert
  // sort_order je Ziel-Artikel-ID; Vater-Artikel-ID → PARENT_COL).
  const order = new Map<string, Record<string, number>>();
  for (const img of images) {
    const rec: Record<string, number> = {};
    const obt = img.orderByTarget ?? {};
    for (const t of targets) {
      const aid = t.articleId;
      const col = colKeyFor(t);
      if (Object.prototype.hasOwnProperty.call(obt, aid)) {
        rec[col] = obt[aid]!;
      } else if (img.assignedTo.includes(aid)) {
        // Fallback: zugeordnet, aber (noch) kein sort_order → ans Ende einreihen.
        rec[col] = Number.MAX_SAFE_INTEGER;
      }
    }
    order.set(img.resourceId, rec);
  }
  // Ränge je Spalte auf 1..N normalisieren (Backend liefert evtl. 0-basiert/lückig;
  // unser Cell-Model erwartet 1-basierte, lückenlose Ränge pro Spalte).
  for (const col of cols) {
    images
      .filter((im) => order.get(im.resourceId)?.[col] !== undefined)
      .sort((a, b) => (order.get(a.resourceId)![col]!) - (order.get(b.resourceId)![col]!))
      .forEach((im, idx) => { order.get(im.resourceId)![col] = idx + 1; });
  }

  let dirty = false;
  const previewCache = new Map<string, string>();
  // Kein eigener Speichern-Button mehr (globaler Pane-Button via Save-Registry). dirty bleibt als
  // State erhalten (1:1-Mockup-Verhalten), updateSave ist daher ein No-Op.
  const updateSave = () => { /* no dedicated save button — global pane save handles persistence */ };

  // ---- Order-Cell-Algorithmus (Port der Mockup-Funktionen) ----
  function colMax(col: string): number {
    let m = 0;
    for (const im of images) { const r = order.get(im.resourceId)?.[col]; if (r && r > m) m = r; }
    return m;
  }
  function ordAdd(rid: string, col: string): void {
    const rec = order.get(rid)!; rec[col] = colMax(col) + 1;
    dirty = true;
  }
  function ordRm(rid: string, col: string): void {
    const rec = order.get(rid)!; const r = rec[col];
    if (r === undefined) return;
    delete rec[col];
    for (const im of images) {
      const orec = order.get(im.resourceId)!;
      if (orec[col] !== undefined && orec[col]! > r) orec[col]!--;
    }
    dirty = true;
  }
  function ordSwap(rid: string, col: string, dir: 1 | -1): void {
    const rec = order.get(rid)!; const r = rec[col];
    if (r === undefined) return;
    const partner = images.find((im) => order.get(im.resourceId)?.[col] === r + dir);
    if (!partner) return;
    order.get(partner.resourceId)![col] = r;
    rec[col] = r + dir;
    dirty = true;
  }

  function ordCellHtml(rid: string, col: string): string {
    const rank = order.get(rid)?.[col];
    if (rank) {
      const isLast = rank === colMax(col);
      return `<span class="kl-ordwrap">`
        + `<button type="button" class="kl-ordcell is-on" data-ordrm data-rid="${escapeHtml(rid)}" data-col="${escapeHtml(col)}" title="Zuordnung entfernen">${rank}</button>`
        + `<span class="kl-ordmini">`
        + `<button type="button" data-ordup data-rid="${escapeHtml(rid)}" data-col="${escapeHtml(col)}" title="nach vorne" ${rank <= 1 ? 'disabled' : ''}><span class="v-icon fa-solid fa-chevron-up"></span></button>`
        + `<button type="button" data-orddown data-rid="${escapeHtml(rid)}" data-col="${escapeHtml(col)}" title="nach hinten" ${isLast ? 'disabled' : ''}><span class="v-icon fa-solid fa-chevron-down"></span></button>`
        + `</span></span>`;
    }
    return `<button type="button" class="kl-ordcell" data-ordadd data-rid="${escapeHtml(rid)}" data-col="${escapeHtml(col)}" title="zuordnen">+</button>`;
  }

  function renderMatrix(body: HTMLElement): void {
    if (images.length === 0) {
      body.innerHTML = `<div class="kli-empty">Keine Bilder an Vater oder Varianten. Bitte in Vario unter Artikel-Detail → Anlagen Bilder hochladen.</div>`;
      return;
    }
    const colHead = targets.map((t) => {
      const col = colKeyFor(t);
      const allOn = images.every((im) => order.get(im.resourceId)?.[col] !== undefined);
      return `<th><label class="kl-colcb ${t.isParent ? 'is-vater' : ''}"><input type="checkbox" class="kl-mcb otto-mx-colhead" data-col="${escapeHtml(col)}" ${allOn ? 'checked' : ''}>${escapeHtml(t.label)}</label></th>`;
    }).join('');
    const rows = images.map((img) => {
      const ready = img.publicUrl ?? previewCache.get(img.resourceId) ?? null;
      const thumb = ready
        ? `<img class="kl-mthumb-img" src="${escapeHtml(ready)}" alt="">`
        : `<div class="kl-mthumb img-proxy-lazy" data-rid="${escapeHtml(img.resourceId)}" data-aid="${escapeHtml(img.owningArticleId)}"><span class="v-icon fa-light fa-image"></span></div>`;
      const cells = targets.map((t) => `<td class="otto-mx-center">${ordCellHtml(img.resourceId, colKeyFor(t))}</td>`).join('');
      return `<tr>
        <td class="otto-mx-imgcell">${thumb}</td>
        <td class="otto-mx-metacell"><button type="button" class="kl-malle otto-mx-all" data-rid="${escapeHtml(img.resourceId)}" title="Allen Zielen zuordnen">alle</button><div class="kl-mdms" title="${escapeHtml(img.alias)}">${escapeHtml(img.alias)}</div></td>
        ${cells}
      </tr>`;
    }).join('');
    // Horizontale Scroll-Position über den Full-Re-Render hinweg erhalten — sonst springt
    // die Matrix bei jeder Zell-Aktion zurück nach links (Bug bei vielen Varianten-Spalten).
    const prevScroll = body.querySelector<HTMLElement>('.kl-matrix-wrap')?.scrollLeft ?? 0;
    body.innerHTML = `
      <div class="kl-img-note">Klicke eine Zelle, um das Bild der Variante zuzuordnen — die Zahl ist die <b>Anzeige-Reihenfolge je Variante</b>. Mit ▲▼ sortieren, Klick auf die Zahl entfernt die Zuordnung.</div>
      <div class="kl-matrix-wrap"><table class="kl-matrix"><thead><tr><th class="kl-mx-lft">Bild</th><th class="kl-mx-lft"></th>${colHead}</tr></thead><tbody>${rows}</tbody></table></div>`;
    const wrap = body.querySelector<HTMLElement>('.kl-matrix-wrap');
    if (wrap && prevScroll) wrap.scrollLeft = prevScroll;
    wireMatrix(body);
    void loadMatrixPreviews(body);
  }

  function wireMatrix(body: HTMLElement): void {
    // Order-Zellen: zuordnen / entfernen / vor / zurück. Jeder Handler mutiert den
    // State und rerendert die Matrix (Mockup-Verhalten: renderBody()).
    body.querySelectorAll<HTMLButtonElement>('button[data-ordadd]').forEach((b) => b.addEventListener('click', () => {
      ordAdd(b.dataset['rid']!, b.dataset['col']!); renderMatrix(body); updateSave();
    }));
    body.querySelectorAll<HTMLButtonElement>('button[data-ordrm]').forEach((b) => b.addEventListener('click', () => {
      ordRm(b.dataset['rid']!, b.dataset['col']!); renderMatrix(body); updateSave();
    }));
    body.querySelectorAll<HTMLButtonElement>('button[data-ordup]').forEach((b) => b.addEventListener('click', () => {
      if (b.disabled) return; ordSwap(b.dataset['rid']!, b.dataset['col']!, -1); renderMatrix(body); updateSave();
    }));
    body.querySelectorAll<HTMLButtonElement>('button[data-orddown]').forEach((b) => b.addEventListener('click', () => {
      if (b.disabled) return; ordSwap(b.dataset['rid']!, b.dataset['col']!, 1); renderMatrix(body); updateSave();
    }));
    // Spalten-Kopf-Checkbox: checked = ALLE Bilder in Zeilen-Reihenfolge (rank 1..N)
    // dieser Spalte zuordnen; unchecked = Spalte leeren.
    body.querySelectorAll<HTMLInputElement>('input.otto-mx-colhead').forEach((cb) => cb.addEventListener('change', () => {
      const col = cb.dataset['col']!;
      if (cb.checked) { let r = 1; for (const im of images) order.get(im.resourceId)![col] = r++; }
      else { for (const im of images) delete order.get(im.resourceId)![col]; }
      dirty = true; renderMatrix(body); updateSave();
    }));
    // Per-Zeile "alle": dieses Bild ALLEN Spalten zuordnen (je colMax(col)+1, sofern
    // noch nicht zugeordnet).
    body.querySelectorAll<HTMLButtonElement>('button.otto-mx-all').forEach((b) => b.addEventListener('click', () => {
      const rid = b.dataset['rid']!; const rec = order.get(rid)!;
      for (const col of cols) { if (rec[col] === undefined) rec[col] = colMax(col) + 1; }
      dirty = true; renderMatrix(body); updateSave();
      showToast('Bild allen Varianten zugeordnet', 'success');
    }));
  }

  async function loadMatrixPreviews(body: HTMLElement): Promise<void> {
    for (const ph of Array.from(body.querySelectorAll<HTMLElement>('.img-proxy-lazy'))) {
      const rid = ph.dataset['rid']; const aid = ph.dataset['aid'];
      if (!rid || !aid || !ph.isConnected) continue;
      const setImg = (url: string): void => {
        if (!ph.isConnected) return;
        ph.classList.remove('kl-thumb-loading');
        ph.innerHTML = `<img class="kl-mthumb-img" src="${escapeHtml(url)}" alt="">`;
      };
      const cached = previewCache.get(rid);
      if (cached) { setImg(cached); continue; }
      try {
        const r = await fetch(`${ctx.supabaseUrl}/functions/v1/vario-dms-image-proxy`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
          body: JSON.stringify({ articleId: aid, resourceId: rid, marketplace: 'otto' }),
        });
        if (!r.ok) continue;
        const url = URL.createObjectURL(await r.blob()); previewCache.set(rid, url);
        setImg(url);
      } catch (e) { console.warn('[otto-list-images] matrix preview proxy failed for', rid, (e as Error).message); }
    }
  }

  async function doSaveMatrix(): Promise<void> {
    // Aus dem order-Map die geordneten Ziele je Bild bauen: PARENT_COL → parentArticleId,
    // sortOrder = der 1-basierte Rang (Backend speichert ihn als sort_order, niedriger = früher).
    const assignments = images.map((img) => {
      const rec = order.get(img.resourceId) ?? {};
      const tgts = Object.entries(rec).map(([col, rank]) => ({ articleId: articleIdForCol(col), sortOrder: rank }));
      return {
        resourceId: img.resourceId,
        mimeType: img.mimeType,
        alias: img.alias,
        targets: tgts,
      };
    });
    const save = await callFn<{ ok: boolean; enqueuedShareJobs: number }>(ctx, 'otto-variant-image-matrix', { action: 'save', parentListingId, assignments });
    // Wirft bei Fehler → der globale saveAll-Handler des Detail-Panes meldet den Fehler aggregiert.
    if (!save.ok) throw new Error(save.error);
    dirty = false; void renderParentMatrix(host, ctx, parentListingId);
  }

  // Kein „Reihenfolge (Vater)"-Sub-Tab mehr: bei Variantenartikeln irrelevant — die Vater-Spalte
  // der Matrix ordnet die Vater-Bilder bereits (order-Zellen je Spalte). Nur die Zuordnungs-Matrix.
  host.innerHTML = `
    <div class="otto-lv2">
      <div class="kl-dbody otto-mx-body"></div>
    </div>`;
  // Bild-Zuordnungs-Matrix speichern — wird über die Save-Registry des Detail-Panes vom globalen
  // „Speichern"-Button ausgeführt. Liest den order-State zur Ausführungszeit.
  ctx.registerSaver?.('images', doSaveMatrix);
  const bodyEl = host.querySelector<HTMLElement>('.otto-mx-body')!;
  renderMatrix(bodyEl);
}

function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
